import React, { useEffect, useRef, memo, useCallback, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';

import Shape from '../../common/Shape';
import Loader from '../../common/Loader';
import ProviderDetails from './ProviderDetails';
import SimpleAvatar from '../../common/SimpleAvatar';
import ClickableElement from '../../ClickableElement';
import { useLazyLoad } from '../../../hocs/useLazyLoad';
import { COMMUNITIES_COMMON } from '../../../constants/strings';
import BackButton from '../../common/BackButton';
import {
  redirectToMyProviders,
  redirectToVisitType,
} from '../../../utils/appointments/redirect-utils';
import { enhance } from './enhance';
import styles from './styles.styl';

function AppointmentMyProviders({
  intl: { formatMessage },
  loadMoreMyProviders,
  initRescheduleFlow,
  paginationLoading,
  fetchMyProviders,
  organizationId,
  providersCount,
  languageTypes,
  providers,
  fullView,
  loading,
  userId,
}) {
  const rootRef = useRef();

  const safeProviderList = useMemo(() => {
    if (!fullView) {
      return providers.slice(0, 3);
    }
    return providers;
  }, [fullView, providers]);

  useEffect(() => {
    if (fullView) {
      fetchMyProviders({ userId, organizationId, limit: 10, offset: 0 });
    }
  }, [fullView]);

  const loadMore = useCallback(() => {
    if (paginationLoading || !fullView) return;
    loadMoreMyProviders({ userId, organizationId });
  }, [paginationLoading, userId, organizationId, fullView]);

  useLazyLoad({ node: rootRef, loadMore, path: ['parentNode', 'parentNode'] });

  const handleViewAllProviders = () => {
    redirectToMyProviders();
  };

  const handleBack = () => {
    redirectToVisitType();
  };

  function renderAvatar(provider) {
    return () => (
      <SimpleAvatar
        src={get(provider, 'avatar.thumbMin')}
        text={provider.displayableName}
        size={44}
        bordered
      />
    );
  }

  return (
    <div
      className={cn(styles.root, {
        [styles.hidden]: !loading && !providers.length,
      })}
      ref={rootRef}
    >
      {fullView && (
        <BackButton
          className={styles.backButton}
          onBack={handleBack}
          minWidth={0}
        />
      )}
      <div className={styles.header}>
        <div className={styles.title}>
          {formatMessage({ id: 'appointments.myProviders' })}
        </div>
        {!fullView && providersCount > 3 && (
          <ClickableElement onClick={handleViewAllProviders}>
            <span className={styles.actionButton}>
              {formatMessage(COMMUNITIES_COMMON.seeAll)}
            </span>
          </ClickableElement>
        )}
      </div>
      {loading && <Loader className={styles.loader} />}
      {paginationLoading && (
        <div className={styles.paginationLoader}>
          <Loader />
        </div>
      )}
      <div className={styles.providerList}>
        {!loading &&
          safeProviderList.length > 0 &&
          safeProviderList.map(provider => {
            const {
              id,
              providerInfo,
              location: { name: locationName },
              service: { name: serviceName },
            } = provider;
            return (
              <Shape
                key={id}
                onClick={() => initRescheduleFlow({ ...provider, userId })}
                renderSecondary={() => `${serviceName}, ${locationName}`}
                renderPrimary={() => (
                  <div className={styles.providerName}>
                    <div>{providerInfo.displayableName}</div>
                    <div className={styles.providerInfo}>
                      <ProviderDetails
                        provider={providerInfo}
                        formatMessage={formatMessage}
                        languageTypes={languageTypes}
                      />
                    </div>
                  </div>
                )}
                renderLeft={renderAvatar(providerInfo)}
              />
            );
          })}
      </div>
    </div>
  );
}

AppointmentMyProviders.propTypes = {
  intl: PropTypes.object,
  loading: PropTypes.bool,
  fullView: PropTypes.bool,
  userId: PropTypes.number,
  providers: PropTypes.array,
  languageTypes: PropTypes.array,
  organizationId: PropTypes.number,
  providersCount: PropTypes.number,
  fetchMyProviders: PropTypes.func,
  paginationLoading: PropTypes.bool,
  initRescheduleFlow: PropTypes.func,
  loadMoreMyProviders: PropTypes.func,
};

export default memo(enhance(AppointmentMyProviders));



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentMyProviders/index.jsx